import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { platformModuleSearch } from '@jfw/business-logic';
import { combineLatest, filter, map, tap } from 'rxjs';
import { AuthService } from '../../../../standalone/services/auth/auth.service';
import { withoutDevBuildOnlyModules } from '../../../../standalone/services/auth/dev-build-only';
import { HomeSearchUiService } from '../../../../standalone/services/home/home-search-ui.service';

interface SearchForm {
  search: FormControl<string | null>;
}

@Component({
  selector: 'app-module-search',
  templateUrl: './module-search.component.html',
  styleUrls: ['./module-search.component.scss'],
  imports: [MatIconModule, MatAutocompleteModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleSearchComponent {
  private router = inject(Router);
  private authService = inject(AuthService);
  private homeSearchUiService = inject(HomeSearchUiService);

  public form = new FormGroup<SearchForm>({
    search: new FormControl(),
  });

  public isHomeRoute = computed(() =>
    ['/', '/home'].includes(this.authService.currentRoute()),
  );

  private leaveHomeRouteEffect = effect(() => {
    if (!this.isHomeRoute()) {
      this.form.reset();
      this.homeSearchUiService.clear();
    }
  });

  private searchText$ = this.form.controls.search.valueChanges.pipe(
    map((value) => value ?? ''),
    tap((value) => {
      if (this.isHomeRoute()) {
        this.homeSearchUiService.setQuery(value);
      }
    }),
  );

  public filteredModules = toSignal(
    combineLatest([
      this.searchText$,
      this.authService.platformUser$.pipe(filter((user) => user !== null)),
    ]).pipe(
      map(([searchText, platformUser]) =>
        withoutDevBuildOnlyModules(platformModuleSearch(searchText, platformUser)),
      ),
    ),
  );

  private navigateToMatchedModule(searchText: string | null): void {
    if (!searchText) {
      return;
    }
    const matchedModule = (this.filteredModules() ?? []).find(
      (module) => module.name.toLowerCase() === searchText.toLowerCase(),
    );
    if (matchedModule) {
      this.router.navigate([matchedModule.routes[0]]);
      this.form.reset();
    }
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.navigateToMatchedModule(event.option.value as string);
  }

  public onSearch(): void {
    if (this.isHomeRoute()) {
      return;
    }
    this.navigateToMatchedModule(this.form.controls.search.value);
  }
}
